import React, { useState, useContext, useEffect } from "react";
import { post } from "../../../helper/Api_helper.js";
import {
  VYLABS_BASE_URL,
  CREATE_CANDIDATE_TRACKING_HISTORY,
  SEARC_JOBS,
} from "../../../helper/url_helper.js";
import DynamicAutocomplete from "../../../Common/AutoSuggestion/DynamicAutocomplete.js";
import SelectInput from "../../../Common/Select-Input/SelectInput.js";
import staticOptions from "../../../Assets/Json/StaticOptions.json";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import { StandardPopup } from "../../../Common/PopupStyles/PopupComponent";
import CALL_STAGE_COLORS from "../../../Common/constants/stageColors.js";
import { applyEllipsis, toCapitalize } from "../../../Common/Common-Function/CommonFunctions.js";
import TextArea from "../../../Common/TextArea/TextArea.js";
import DynamicCallStage from "../../../Common/DynamicCallStage/DynamicCallStage";

function CandidatesAssignJob({
  isOpen,
  setAssignJobModalOpen,
  selectedCandidates,
  jobId,
  jobTitle,
}) {
  const { StaticOptions = {} } = staticOptions;
  const { CandidateTrackingStatus = [] } = StaticOptions || {};
  const callStageOptions = CandidateTrackingStatus.map((option) => ({
    ...option,
    colorIndicator: CALL_STAGE_COLORS[option.title] || "#6B7280",
  }));

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobTouched, setJobTouched] = useState(false);
  const [jobError, setJobError] = useState("");
  const [jobListOptions, setJobListOptions] = useState([]);
  const [jobOptionLoading, setJobOptionLoading] = useState(false);
  const [callStage, setCallStage] = useState("");
  const [callStageTouched, setCallStageTouched] = useState(false);
  const [callStageError, setCallStageError] = useState("");
  const [remark, setRemark] = useState("");
  const { userDetails } = useContext(DynamicContext);

  useEffect(() => {
    if (jobId && jobTitle) {
      const preselectedJob = { label: jobTitle, value: jobId };
      setSelectedJobId(preselectedJob);
      setJobListOptions([preselectedJob]);
    } else {
      setSelectedJobId(null);
      setJobListOptions([]);
    }
  }, [jobId, jobTitle, isOpen]);

  useEffect(() => {
    if (jobTouched) {
      setJobError(selectedJobId && selectedJobId.value ? "" : "Job is required");
    }
  }, [selectedJobId, jobTouched]);

  useEffect(() => {
    if (callStageTouched) {
      setCallStageError(callStage ? "" : "Call Stage is required");
    }
  }, [callStage, callStageTouched]);

  const handleCloseJobAssign = () => {
    setCallStage("");
    setCallStageTouched(false);
    setCallStageError("");
    setSelectedJobId(null);
    setJobTouched(false);
    setJobError("");
    setRemark("");
    setAssignJobModalOpen(false);
  };

  const handleJobChange = (value) => {
    setSelectedJobId(value);
    setJobTouched(true);
  };

  const handleJobBlur = () => {
    setJobTouched(true);
  };

  const handleCallStageChange = (e) => {
    setCallStage(e.target.value);
    setCallStageTouched(true);
  };

  const handleCallStageBlur = () => {
    setCallStageTouched(true);
  };

  const handleRemarkChange = (e) => {
    setRemark(e.target.value);
  };

  const validateForm = () => {
    let valid = true;
    if (!selectedJobId || !selectedJobId.value) {
      setJobTouched(true);
      setJobError("Job is required");
      Toast("error", "Please select a job.");
      valid = false;
    }
    if (!callStage) {
      setCallStageTouched(true);
      setCallStageError("Call Stage is required");
      Toast("error", "Please select a call stage.");
      valid = false;
    }
    if (!selectedCandidates || selectedCandidates.length === 0) {
      Toast("error", "No candidate selected.");
      valid = false;
    }
    return valid;
  };

  const handleAssignJob = async () => {
    if (!validateForm()) return;

    try {
      const payloads = selectedCandidates.map((candidate) => {
        const payload = {
          assignedRecruiterID: userDetails?.id,
          candidateID: candidate.id || candidate.candidate_id || "",
          fileID: candidate.file_id || "",
          jobId: selectedJobId.value,
          stage: callStage,
        };
        if (remark.trim()) {
          payload.remarks = remark.trim();
        }
        return payload;
      });

      await Promise.all(
        payloads.map((payload) =>
          post(VYLABS_BASE_URL, CREATE_CANDIDATE_TRACKING_HISTORY, payload)
        )
      );
      Toast("success", "Job assigned successfully");
      handleCloseJobAssign();
    } catch (error) {
      console.error("Error adding tracking history:", error);
    }
  };

  const handleJobInputChange = async (_, value) => {
    if (value && value.length >= 3 && (!jobId || value !== jobTitle)) {
      fetchJobOptions(value);
    } else if (!value && !jobId) {
      setJobListOptions([]);
    }
  };

  const fetchJobOptions = async (inputValue) => {
    if (!inputValue) return;
    setJobOptionLoading(true);
    try {
      const response = await post(VYLABS_BASE_URL, SEARC_JOBS, {
        query: inputValue,
      });
      const data = response.jobs || [];
      const fetchOptions = [
        ...new Map(
          data.map((item) => [item.id, { label: item.title, value: item.id }])
        ).values(),
      ];
      setJobListOptions(fetchOptions);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setJobOptionLoading(false);
    }
  };

  return (
    <StandardPopup
      isOpen={isOpen}
      onClose={handleCloseJobAssign}
      onSubmit={handleAssignJob}
      title={
        selectedCandidates.length === 1
          ? `Assign Job to ${applyEllipsis(toCapitalize(selectedCandidates[0]?.name), 30) || "Candidate"}`
          : "Assign Job to Selected Candidates"
      }
      submitLabel="Assign Job"
      cancelLabel="Cancel"
      width="40vw"
    >
      <div className="popup-field">
        <label className="popup-label">Job</label>
        <DynamicAutocomplete
          onChange={handleJobChange}
          value={selectedJobId}
          placeholder="Search Job"
          noOptionsText="No jobs found."
          minCharsForSearch={3}
          onInputChange={handleJobInputChange}
          options={jobListOptions}
          loading={jobOptionLoading}
          onBlur={handleJobBlur}
          readOnly={true}         
          sx={{
            mt: "5px",
            ".MuiOutlinedInput-root": {
              borderRadius: "12px",
              height: "40px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid var(--border__color) !important",
                borderRadius: "12px",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6860ee",
                borderRadius: "12px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7a70f2",
                borderRadius: "12px",
              },
            },
            ".MuiInputBase-input": {
              color: "var(--header__color)",
              padding: "8px 12px !important",
              height: "40px",
              fontFamily: "Satoshi !important",
            },
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "var(--body__background)",
                color: "var(--header__color)",
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                border: "1px solid var(--header__color)",
              },
            },
            listbox: {
              sx: {
                "& li": {
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 16px",
                  color: "var(--header__color)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                },
              },
            },
          }}
          error={jobTouched && Boolean(jobError)}
          errorMessage={""}
        />
        {jobTouched && jobError && (
          <div style={{ color: 'red', fontSize: '16px', marginBottom: '6px', textAlign: 'left' }}>{jobError}</div>
        )}
      </div>
      <div className="popup-field">
        <label className="popup-label">Call Stage</label>
        <DynamicCallStage
          placeholder="Call Stage"
          name="callStage"
          value={callStage}
          onChange={handleCallStageChange}
          onBlur={handleCallStageBlur}
          selectStyle={{ width: "100%", height: "48px" }}
          error={callStageTouched && Boolean(callStageError)}
          errorMessage={
            callStageTouched && callStageError
              ? callStageError
              : ""
          }
        />
      </div>
      {
         selectedCandidates.length === 1 &&
         <div className="popup-field">
         <TextArea
           label="Remark (Optional)"
           placeholder="Enter remark"
           value={remark}
           onChange={handleRemarkChange}
           rows={1}
           style={{
             borderRadius: "12px",
             border: "1px solid var(--border__color)",
             color: "var(--header__color)",
             fontFamily: "Satoshi",
           }}
           className="popup-textarea"
         />
       </div>
      }

    </StandardPopup>
  );
}

export default CandidatesAssignJob;